import React, { useState } from "react";
import { Input, Button, Table, Label } from "reactstrap";
import { FiltroTipoHabilitacion, coincideTipo } from "./Habilitacion/TiposHabilitacion";
import { v4 as uuidv4 } from "uuid";

/**
 * Captura de material por CANTIDAD: tipo, material, color (solo tela) y
 * cantidad. Sin rollos ni QR. La usan salida, devolucion y apartados.
 * Con habilitacion se puede acotar la lista por tipo (BOTÓN, CIERRE...).
 *
 * Props:
 *   renglones, setRenglones
 *   telas, habilitacion, colores   catalogos
 *   soloTipo        "Telas" | "Habilitacion" para fijar el tipo (opcional)
 *   limiteDe(r)     -> { maximo, texto } lo que se puede usar de ese material (opcional)
 *   opcionesDe(tipo) -> lista de materiales permitidos para ese tipo (opcional)
 *   coloresDe(r)    -> colores permitidos para ese renglon (opcional)
 */
export const renglonVacio = (tipo = "Telas") => ({
  id: uuidv4(), tipoMaterial: tipo, material: "", colores: "", cantidad: "",
});

export default function CapturaMateriales({
  renglones, setRenglones, telas, habilitacion, colores, soloTipo, limiteDe, opcionesDe, coloresDe,
}) {
  const [filtroTipoHab, setFiltroTipoHab] = useState("");
  const cambia = (id, campo, valor) =>
    setRenglones(
      renglones.map((r) => {
        if (r.id !== id) return r;
        // Cambiar de tipo o de material invalida lo que dependia de el.
        if (campo === "tipoMaterial") return { ...r, tipoMaterial: valor, material: "", colores: "" };
        if (campo === "material") return { ...r, material: valor, colores: "" };
        return { ...r, [campo]: valor };
      })
    );
  const agrega = () => setRenglones([...renglones, renglonVacio(soloTipo || renglones[renglones.length - 1]?.tipoMaterial)]);
  const quita = (id) => setRenglones(renglones.filter((r) => r.id !== id));

  // El material ya elegido se queda en la lista aunque no sea del tipo filtrado.
  const materiales = (tipo, elegido) =>
    (opcionesDe ? opcionesDe(tipo) : tipo === "Telas" ? telas : habilitacion)
      .filter((m) => tipo === "Telas" || m._id === elegido || coincideTipo(m, filtroTipoHab))
      .sort((a, b) => ((a.nombre || "") > (b.nombre || "") ? 1 : -1));
  const coloresPara = (r) => (coloresDe ? coloresDe(r) : colores).slice().sort((a, b) => (a.name > b.name ? 1 : -1));

  const conHabilitacion = renglones.some((r) => r.tipoMaterial === "Habilitacion");

  return (
    <>
    {conHabilitacion ? (
      <div style={{ maxWidth: 320, marginBottom: 8 }}>
        <Label>Tipo de habilitación</Label>
        <FiltroTipoHabilitacion bsSize="sm" value={filtroTipoHab} onChange={setFiltroTipoHab} />
      </div>
    ) : null}
    <Table borderless size="sm">
      <thead>
        <tr>
          {soloTipo ? undefined : <th style={{ width: "14%" }}>Tipo</th>}
          <th style={{ width: "32%" }}>Material</th>
          <th style={{ width: "18%" }}>Color</th>
          <th style={{ width: "14%" }}>Cantidad</th>
          <th style={{ width: "14%" }}>{limiteDe ? "Disponible" : ""}</th>
          <th style={{ width: "8%" }} />
        </tr>
      </thead>
      <tbody>
        {renglones.map((r) => {
          const limite = limiteDe && r.material ? limiteDe(r) : null;
          const excede = limite && parseFloat(r.cantidad) > limite.maximo;
          return (
            <tr key={r.id}>
              {soloTipo ? undefined : (
                <td>
                  <Input type="select" value={r.tipoMaterial} onChange={(e) => cambia(r.id, "tipoMaterial", e.target.value)}>
                    <option value="Telas">Tela</option>
                    <option value="Habilitacion">Habilitacion</option>
                  </Input>
                </td>
              )}
              <td>
                <Input type="select" value={r.material} onChange={(e) => cambia(r.id, "material", e.target.value)}>
                  <option value="">Selecciona</option>
                  {materiales(r.tipoMaterial, r.material).map((m) => (
                    <option value={m._id} key={m._id}>
                      {[m.codigo, m.nombre, m.descripcion].filter((x) => x && x !== "NA" && x !== "-").join(" / ")}
                    </option>
                  ))}
                </Input>
              </td>
              <td>
                <Input
                  type="select"
                  value={r.colores}
                  disabled={r.tipoMaterial !== "Telas"}
                  onChange={(e) => cambia(r.id, "colores", e.target.value)}
                >
                  <option value="">{r.tipoMaterial === "Telas" ? "Selecciona" : "No aplica"}</option>
                  {coloresPara(r).map((c) => (
                    <option value={c._id} key={c._id}>{c.name}</option>
                  ))}
                </Input>
              </td>
              <td>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={r.cantidad}
                  onChange={(e) => cambia(r.id, "cantidad", e.target.value)}
                  style={excede ? { borderColor: "#dc2626" } : undefined}
                />
              </td>
              <td style={{ color: excede ? "#dc2626" : "#64748b", fontWeight: excede ? 600 : 400, fontSize: 13 }}>
                {limite ? limite.texto : ""}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                <Button title="Quitar renglón" size="sm" color="danger" disabled={renglones.length === 1} onClick={() => quita(r.id)}>
                  <i className="fas fa-minus" />
                </Button>{" "}
                <Button title="Agregar renglón" size="sm" color="info" onClick={agrega}>
                  <i className="fas fa-plus" />
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
    </>
  );
}

/** Renglones listos para mandar al back (sin vacios). */
export const renglonesParaEnviar = (renglones) =>
  renglones
    .filter((r) => r.material && parseFloat(r.cantidad) > 0)
    .map((r) => ({
      tipoMaterial: r.tipoMaterial,
      material: r.material,
      colores: r.tipoMaterial === "Telas" ? r.colores || undefined : undefined,
      cantidad: parseFloat(r.cantidad),
      ...(r.costo !== undefined ? { costo: parseFloat(r.costo) || 0 } : {}),
    }));

/** Falta color en una tela: sin color no se sabe de que saldo sacar. */
export const telaSinColor = (renglones) =>
  renglones.find((r) => r.tipoMaterial === "Telas" && r.material && parseFloat(r.cantidad) > 0 && !r.colores);
